import { useContext, useEffect, useState } from 'react';
import AuthContext from '../contexts/AuthContext';
import { AlertContext } from '../contexts/AlertContext';
import Accordion from 'react-bootstrap/Accordion';
import Button from 'react-bootstrap/Button'
import Card from 'react-bootstrap/Card';
import Col from 'react-bootstrap/Col';
import Container from 'react-bootstrap/Container';
import Form from 'react-bootstrap/Form'
import Row from 'react-bootstrap/Row';
import { ChevronDown, ChevronUp } from 'react-bootstrap-icons';
import ProductImages from '../components/ProductImages';
import { Storage } from 'aws-amplify';

const ProductMetadataForm = ({ collection, productSKU }) => {
  const { authState } = useContext(AuthContext);
  const { dispatch } = useContext(AlertContext);
  const closeAlert = () => dispatch({ type: 'close' }); // HACK: needed to pass reducer dispatch() func
  const [activeCard, setActiveCard] = useState(0);
  const [imageUploading, setImageUploading] = useState(false);
  const [productMetadata, setProductMetadata] = useState({});
  const {
    brand,
    description,
    displaySKU,
    images,
    name,
    price,
    productId,
    productLine,
    productType,
    productViewId,
    productURL,
    sku,
  } = productMetadata;
  const readOnly = authState?.userPermissions[collection]?.update === false;

  // XXX: get products metadata
  useEffect(() => {
    Storage.get(`p/${collection}/${productSKU}/metadata/index.json`, {
      download: true,
      cacheControl: 'no-cache',
    })
      .then((res) => {
        res.Body.text().then((resText) => {
          const productMetadata = JSON.parse(resText);
          setProductMetadata(productMetadata);
        });
      })
      .catch((err) => console.warn(err));
  }, [collection, productSKU]);

  const toggleAccordion = (eventKey) => {
    // if toggling currently expanded, set to null so nothing is toggled; otherwise expand new card
    setActiveCard(eventKey === activeCard ? null : eventKey);
  };

  const handleChange = (e) => {
    const key = e.target.id;
    const value = e.target.value;
    setProductMetadata({ ...productMetadata, [key]: value });
  };

  const writeJSON = async (object, s3Path) => {
    try {
      await Storage.remove(s3Path);
      const res = await Storage.put(s3Path, JSON.stringify(object, null, 2), {
        level: 'public',
        contentType: 'application/json',
        cacheControl: 'no-cache',
      });
      console.log('Product Metadata Updated:', res?.key);
      dispatch({
        type: 'success',
        title: 'Metadata Saved',
        message: `Product metadata saved.`,
        onConfirm: closeAlert,
      });
    } catch (err) {
      console.warn('S3 write Error:', err);
      dispatch({
        type: 'danger',
        title: 'Metadata NOT Saved',
        message: `ERROR: ${err}.`,
        onConfirm: closeAlert,
      });
    }
  };

  const handleSubmit = () => {
    const productMetadataS3Path = `p/${collection}/${productSKU}/metadata/index.json`;
    writeJSON(productMetadata, productMetadataS3Path);
  };

  const handleAddImage = async (productImage, onSuccess) => {
    setImageUploading(true);
    try {
      const { file, imageShows, heroImage } = productImage;
      const s3KeyName = `p/${collection}/${productSKU}/images/${file.name}`;
      const extension = file?.name?.split('.')?.pop();
      const validExtensions = ['jpg', 'jpeg', 'png', 'webp'];
      if (validExtensions.includes(extension) === false) {
        // eslint-disable-next-line no-throw-literal
        throw 'Invalid file type. Must upload .jpg, .png, or .webp';
      }
      const contentType = `image/${
        ['.jpg', '.jpeg'].includes(extension) ? 'jpeg' : extension
      }`;
      const res = await Storage.put(s3KeyName, file, {
        level: 'public',
        contentType,
      });
      // add timestamp as cachebreaker
      const savedImageURL = `https://cdn.facecake.com/${
        res.key
      }?v=${Date.now().toString(36)}`;
      console.log('saved', savedImageURL);
      // overwrite existing if URL matches
      let imageAdded = false;
      let heroFound = heroImage; // initialize to true if new file is hero
      const newImages = productMetadata?.images?.map((image) => {
        // if the new img is hero image, unset the rest
        if (heroImage === true) {
          image.heroImage = false;
          heroFound = true;
        }
        // if new img url (without cachebreaker) matches, update
        if (image?.url?.split('?')?.[0] === savedImageURL.split('?')[0]) {
          image = { imageShows, heroImage, url: savedImageURL };
          imageAdded = true;
        }
        return image;
      });
      // if it wasn't added, add it now
      if (imageAdded === false) {
        newImages.push({ imageShows, heroImage, url: savedImageURL });
      }
      // if no hero found, set the first image to hero
      if (heroFound === false) {
        newImages[0].heroImage = true;
      }
      setProductMetadata({ ...productMetadata, images: newImages });
      setImageUploading(false);
      onSuccess();
    } catch (err) {
      console.warn('S3 write Error:', err);
      // show alert modal
      dispatch({
        type: 'danger',
        title: 'Product Image Upload Error',
        message: `ERROR: ${err}`,
        onConfirm: closeAlert,
      });
      setImageUploading(false);
    }
  };

  const handleDeleteImage = (deleteIndex) => {
    console.log(images);
    images.splice(deleteIndex, 1);
    setProductMetadata({ ...productMetadata, images: images });
  };

  return (
    <Container>
      <Row>
        <Col>
          <Accordion defaultActiveKey="0">
            <Card>
              <Accordion.Toggle
                as={Card.Header}
                eventKey="0"
                onClick={() => toggleAccordion(0)}
                style={{
                  backgroundColor:
                    activeCard === 0
                      ? 'var(--secondary-light)'
                      : 'var(--primary-light)',
                }}
              >
                PRODUCT INFORMATION
                <div className="float-right">
                  {activeCard === 0 ? <ChevronUp /> : <ChevronDown />}
                </div>
              </Accordion.Toggle>
              <Accordion.Collapse eventKey="0">
                <Card.Body>
                  <Form>
                    <Form.Row>
                      <Form.Group as={Col} controlId="productId">
                        <Form.Label>FaceCake Product ID</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Enter product ID"
                          value={productId || ''}
                          readOnly={readOnly}
                          onChange={handleChange}
                        />
                      </Form.Group>

                      <Form.Group as={Col} controlId="productViewId">
                        <Form.Label>Product View ID</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Enter product view ID"
                          value={productViewId || ''}
                          readOnly={readOnly}
                          onChange={handleChange}
                        />
                      </Form.Group>
                    </Form.Row>

                    <Form.Row>
                      <Form.Group as={Col} controlId="sku">
                        <Form.Label>SKU</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Enter SKU"
                          value={sku || ''}
                          readOnly={readOnly}
                          onChange={handleChange}
                        />
                      </Form.Group>

                      <Form.Group as={Col} controlId="displaySKU">
                        <Form.Label>Display SKU</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Enter Display SKU"
                          value={displaySKU || ''}
                          readOnly={readOnly}
                          onChange={handleChange}
                        />
                      </Form.Group>
                    </Form.Row>

                    <Form.Row>
                      <Form.Group as={Col} controlId="brand">
                        <Form.Label>Brand</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Enter brand"
                          value={brand || ''}
                          readOnly={readOnly}
                          onChange={handleChange}
                        />
                      </Form.Group>

                      <Form.Group as={Col} controlId="productType">
                        <Form.Label>Product Type</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Enter product type"
                          value={productType || ''}
                          readOnly={readOnly}
                          onChange={handleChange}
                        />
                      </Form.Group>
                    </Form.Row>

                    <Form.Row>
                      <Form.Group as={Col} controlId="name">
                        <Form.Label>Product Name</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Enter product name"
                          value={name || ''}
                          readOnly={readOnly}
                          onChange={handleChange}
                        />
                      </Form.Group>

                      <Form.Group as={Col} controlId="productLine">
                        <Form.Label>Product Line</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Enter product line"
                          value={productLine || ''}
                          readOnly={readOnly}
                          onChange={handleChange}
                        />
                      </Form.Group>
                    </Form.Row>

                    <Form.Row>
                      <Form.Group as={Col} controlId="price">
                        <Form.Label>Price</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Enter product price"
                          value={price || ''}
                          readOnly={readOnly}
                          onChange={handleChange}
                        />
                      </Form.Group>

                      <Form.Group as={Col} controlId="productURL">
                        <Form.Label>Product URL</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Enter product URL"
                          value={productURL || ''}
                          readOnly={readOnly}
                          onChange={handleChange}
                        />
                      </Form.Group>
                    </Form.Row>
                    <Form.Row>
                      <Form.Group as={Col} controlId="description">
                        <Form.Label>Product Description</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={5}
                          value={description || ''}
                          readOnly={readOnly}
                          onChange={handleChange}
                        />
                      </Form.Group>
                    </Form.Row>
                  </Form>
                </Card.Body>
              </Accordion.Collapse>
            </Card>
            <Card>
              <Accordion.Toggle
                as={Card.Header}
                eventKey="1"
                onClick={() => toggleAccordion(1)}
                style={{
                  backgroundColor:
                    activeCard === 1
                      ? 'var(--secondary-light)'
                      : 'var(--primary-light)',
                }}
              >
                PRODUCT IMAGES
                <div className="float-right">
                  {activeCard === 1 ? <ChevronUp /> : <ChevronDown />}
                </div>
              </Accordion.Toggle>
              <Accordion.Collapse eventKey="1">
                <Card.Body>
                  <ProductImages
                    productImages={images}
                    readOnly={readOnly}
                    imageUploading={imageUploading}
                    onUpload={handleAddImage}
                    onDelete={handleDeleteImage}
                  />
                </Card.Body>
              </Accordion.Collapse>
            </Card>
            <Card>
              <Card.Body>
                <div className="mx-auto float-right">
                  {/* <Button
                    variant={readOnly ? 'outline-secondary' : 'outline-primary'}
                    disabled={readOnly}
                  >
                    Reset
                  </Button> */}
                  &nbsp;&nbsp;
                  <Button
                    variant={readOnly ? 'secondary' : 'primary'}
                    disabled={readOnly}
                    onClick={handleSubmit}
                  >
                    Save Metadata
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Accordion>
        </Col>
      </Row>
    </Container>
  );
};

export default ProductMetadataForm;